import React, { useContext, useEffect, useState } from 'react'
import Error from 'next/error'
import { fetchCollectionHeroTag } from '@api/graphqlContentFul'
import { fetchContentfulProductsBreakdownByHandle } from '@api/graphqlContentFul/productsBreakdown'
import logBuildError from '@helpers/misc/logBuildError'
import { getValueFromObject } from '@helpers/utils'
import { isProdEnv } from '@helpers/env'
import Head from '@components/Head'
import Collections from '@components/collection'
import NotFound from '../404'
import { useRouter } from 'next/router'
import AppContext from '@components/AppContext'
import {
  getCollectionByHandle,
  getCollectionHandles
} from '../../server/api/graphqlContentService/collections'
import {
  Collection,
  CollectionConfig,
  CollectionHandles,
  CollectionListMeta
} from 'types/collection'
import { CustomError } from '@helpers/errors'
import { HeaderProps } from 'types/header'
import { ProductsBreakdownProps } from '@components/ui/ProductsBreakdown'
import { CollectionMenuLinksProps } from '@components/collection/CollectionMenuLinks'
import axios, { AxiosError } from 'axios'
import { FilterBuild } from '@components/collection/CollectionFilter/FiltersSidePanel/types'
import { getFilterBuildAdapter } from '@helpers/collections/adapter'
import { getCollectionProductCategory } from '@helpers/collections/getCollectionProductCategory'
import { transformCollections } from '@helpers/collections/adapter/transformCollections'
import { ProductsBreakdownProps as ApiProductsBreakdownProps } from 'types/contentful/graphQlApi'

const handleCollectionsBuildError = logBuildError('collections')

const MAP_COLLECTION_NAVIGATON = {
  trousers: {
    title: 'Trousers',
    handle: '/collections/trousers'
  },
  denim: {
    title: 'Denim',
    handle: '/collections/denim'
  },
  tops: {
    title: 'Tops',
    handle: '/collections/tops'
  },
  shorts: {
    title: 'Shorts',
    handle: '/collections/shorts'
  },
  accessories: {
    title: 'Accessories',
    handle: '/collections/accessories'
  }
}

const EXCLUDE_COLLECTIONS_NAVIGATION = ['new-arrivals']

function getCollectionNavigationLinks(
  header: HeaderProps,
  collectionHandle: Collection['handle'],
  collectionCategory: Collection['product_category']
) {
  if (
    !collectionCategory ||
    EXCLUDE_COLLECTIONS_NAVIGATION.includes(collectionHandle)
  ) {
    return []
  }

  const collectionNav = header?.items?.filter(
    item => item?.title.toLowerCase() === collectionCategory.toLowerCase()
  )

  const primaryLinksExpections = ['denim', 'shorts', 'accessories', 'tops']

  const getHeaderGridLinksType = () => {
    return primaryLinksExpections.includes(collectionCategory)
      ? 'primary'
      : 'secondary'
  }

  const collectionNavLinks =
    (collectionNav?.length > 0 &&
      collectionNav[0].grid.gridLinks.items.filter(
        item => item.variant === getHeaderGridLinksType()
      )[0]?.links.items) ||
    []

  const cleanedCollectionNavLinks =
    (collectionNavLinks.length > 0 &&
      collectionNavLinks.filter(
        link => link.title.toLowerCase() !== 'view all'
      )) ||
    []

  const parentCategoryNavLink =
    MAP_COLLECTION_NAVIGATON[
      collectionCategory?.toLowerCase() as keyof typeof MAP_COLLECTION_NAVIGATON
    ]

  return parentCategoryNavLink
    ? [parentCategoryNavLink, ...cleanedCollectionNavLinks]
    : cleanedCollectionNavLinks
}

type FetchProductsBreakdownProps = {
  pageHandle: string
  onError?: (error: CustomError) => void
  preview?: boolean
}

type ProductBreakdownItem = {
  categories: Pick<ProductsBreakdownProps, 'categories'> | { items: any[] }
  visible?: boolean | null
}

function transformProductBreakdown(
  productBreakdownData: ApiProductsBreakdownProps | undefined
) {
  const productBreakdownItem = getValueFromObject<ProductBreakdownItem>(
    productBreakdownData,
    'productsBreakdown.items[0]',
    {
      categories: { items: [] }
    }
  )
  return {
    ...productBreakdownItem,
    visible: productBreakdownItem?.visible ?? true
  }
}

async function fetchProductsBreakdown({
  pageHandle,
  onError = () => undefined,
  preview
}: FetchProductsBreakdownProps): Promise<
  Pick<ProductBreakdownItem, 'categories'>
> {
  try {
    const response = await fetchContentfulProductsBreakdownByHandle({
      pageHandle,
      preview
    })

    return transformProductBreakdown(response)
  } catch (error) {
    onError(error as CustomError)
    return { categories: { items: [] } }
  }
}

type FetchHeroTagProps = {
  pageHandle: string
  onError?: (error: CustomError) => void
  preview?: boolean
}

async function fetchHeroTag({
  pageHandle,
  onError = () => undefined,
  preview
}: FetchHeroTagProps) {
  const response = await fetchCollectionHeroTag({
    pageHandle,
    preview
  }).catch(error => {
    onError(error)
    return { productsBreakdown: { items: [] } }
  })
  return getValueFromObject(response, 'collectionHero.items[0]', {})
}

export type CollectionsPageProps = {
  collectionData: {
    collections: Collection[]
    collectionConfig: CollectionConfig
    title: string
    byline: string
    description: CollectionConfig['detailed_description']
    meta: CollectionListMeta
  }
  collectionHero: {
    badge: {
      title: string
    }
  }
  productsBreakdown: ProductsBreakdownProps
  navigationLinks: CollectionMenuLinksProps['navigationLinks']
  productIds: number[]
  productTypes: string[]
  collectionHandle: string
  statusCode: number
  headers: any
  filterBuild: FilterBuild
}

function CollectionsPage(props: CollectionsPageProps) {
  const { header } = useContext(AppContext)
  const [contentfulPreviewData, setContentfulPreviewData] = useState({})
  const { query } = useRouter()
  const { collectionData, collectionHandle } = props

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const allRequests = [
          fetchProductsBreakdown({
            pageHandle: collectionHandle,
            preview: true
          }),
          fetchHeroTag({
            pageHandle: collectionHandle,
            preview: true
          })
        ]
        const [productsBreakdown, collectionHero, navigationLinks] =
          await Promise.all(allRequests)

        setContentfulPreviewData({
          productsBreakdown,
          collectionHero,
          navigationLinks
        })
      } catch (e) {
        console.error(e)
      }
    }
    if (query.preview === 'true') {
      fetchPreview()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.preview, query.productHandle])

  if (props.statusCode === 404) return <NotFound />

  if (props.statusCode !== 200)
    return <Error headers={props.headers} statusCode={props.statusCode} />

  const collectionProductCategory = getCollectionProductCategory(
    collectionData.collections
  )
  const navigationLinks = getCollectionNavigationLinks(
    header,
    collectionHandle,
    collectionProductCategory
  )

  const collectionProps = {
    ...props,
    navigationLinks,
    ...contentfulPreviewData
  }

  return (
    <>
      <Head
        title={collectionData.meta.seo_title || collectionData.title}
        image={collectionData.meta.seo_img}
        desc={
          collectionData.meta.seo_description ||
          `Spoke ${collectionData.title} Collection`
        }
        includeNoFollow={false}
      />
      <Collections {...collectionProps} />
    </>
  )
}

function collectionHandlesManager() {
  let handles: CollectionHandles

  return {
    get: async function () {
      if (!handles) {
        handles = (await getCollectionHandles()) || []
      }

      return handles
    }
  }
}
const handlesManager = collectionHandlesManager()

export async function getStaticPaths() {
  const collectionHandles = await handlesManager.get()

  const paths = collectionHandles.map(collection => ({
    params: { collectionHandle: collection.handle }
  }))

  return { paths, fallback: false }
}

type CollectionPageProps = {
  params: {
    collectionHandle: string
  }
}

export async function getStaticProps({ params }: CollectionPageProps) {
  const { collectionHandle } = params
  try {
    const collectionHandles = await handlesManager.get()

    const collectionHandleData = collectionHandles?.find(
      collection => collection.handle === collectionHandle
    )
    const groupTag = collectionHandleData?.group_tag || ''
    const pageData = await getCollectionByHandle(collectionHandle, groupTag)

    if ('errors' in pageData) {
      throw new CustomError(
        `API error - ON.stringify for collectionHandle '${collectionHandle}' and groupTag '${groupTag}' with errors ${JSON.stringify(
          pageData.errors
        )} }`
      )
    }

    if (!pageData.collections.length) {
      handleCollectionsBuildError({
        errorLevel: 'warning',
        properties: {
          handle: collectionHandle,
          message: 'Empty collection'
        }
      })
    }

    if (!pageData.collectionConfig) {
      handleCollectionsBuildError({
        errorLevel: 'warning',
        properties: {
          handle: collectionHandle,
          message: 'Collection missing config data'
        }
      })
    }

    const filteredCollections = pageData.collections.filter(
      collection => !!collection.items?.length
    )

    const productTypes = filteredCollections.map(
      collection => collection.product_type
    )
    const productIds = filteredCollections.flatMap(collection =>
      collection.items.map(item => item.id)
    )

    const transformedCollections = transformCollections(filteredCollections)

    const filterBuild = getFilterBuildAdapter(transformedCollections)

    const filterBuildHasMultipleCategoryTypes = filterBuild.length > 1

    const collectionData = {
      title: pageData.title,
      byline: pageData.byline,
      description: pageData.collectionConfig?.detailed_description,
      collections: transformedCollections,
      meta: {
        header: pageData.title,
        seo_title: pageData.collectionConfig?.seo_title,
        seo_description: pageData.collectionConfig?.seo_description,
        seo_img: pageData.collectionConfig?.seo_img,
        show_product_type:
          pageData.collectionConfig?.show_product_type_on_each_product || false,
        filters: {
          show: filterBuild.length > 0,
          multi: filterBuildHasMultipleCategoryTypes
        },
        page_crawl: pageData.collectionConfig?.page_crawl || false
      }
    }

    const productsBreakdown = await fetchProductsBreakdown({
      pageHandle: collectionHandle,
      onError: error => {
        handleCollectionsBuildError({
          errorLevel: 'error',
          properties: {
            handle: collectionHandle,
            message: `${error.name} - ${error.message}`,
            error
          }
        })
      }
    })

    const collectionHero = await fetchHeroTag({
      pageHandle: collectionHandle,
      onError: error => {
        handleCollectionsBuildError({
          errorLevel: 'error',
          properties: {
            handle: collectionHandle,
            message: `${error.name} - ${error.message}`,
            error
          }
        })
      }
    })

    return {
      props: {
        filterBuild,
        collectionHandle,
        collectionData,
        productsBreakdown,
        collectionHero,
        href: `/collections/${collectionHandle}`,
        productIds,
        productTypes,
        statusCode: 200
      }
    }
  } catch (e) {
    const error = e as typeof CustomError | AxiosError
    const handle = collectionHandle

    console.error(error)
    if (
      (error instanceof CustomError && error.status === 404) ||
      (axios.isAxiosError(error) && error.response?.status === 404)
    ) {
      handleCollectionsBuildError({
        errorLevel: 'warning',
        properties: {
          handle,
          message: '404 - Collection does not exist'
        }
      })
    } else {
      const errorMessage =
        (error instanceof CustomError && error.message) ||
        (axios.isAxiosError(error) && error.response?.data?.message)
      handleCollectionsBuildError({
        errorLevel: 'critical',
        properties: {
          handle,
          ...(axios.isAxiosError(error) && error.response
            ? {
                path: error.request.path,
                error: error.response.data.error || error.response.data.message
              }
            : { error: errorMessage })
        }
      })
      if (process.env.NODE_ENV === 'production' && isProdEnv()) {
        throw new CustomError('Collection page failed to export')
      }
    }

    return {
      props: {
        headers:
          axios.isAxiosError(error) && error.response
            ? error.response.headers
            : '',
        statusCode:
          axios.isAxiosError(error) && error.response
            ? error.response.status
            : 404
      }
    }
  }
}

export default CollectionsPage
